/**
 * PDFs — the shareable documents dealers get buttons for in the app.
 *
 * Add as many as you like; each has its own button title. Files are uploaded to
 * the sync worker (stored on Cloudflare, free bandwidth) and the list lives in
 * `app_config/offers`, which the dealer app reads live. Removing an entry
 * deletes the stored file and the button.
 */
import { useEffect, useState } from 'react';
import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { COLLECTIONS, OFFERS_DOC, type OfferPdf, type OffersConfig } from '@mitang/shared';
import { auth, db } from '../lib/firebase';
import { Card, SectionHeader, Button } from '../components/ui';
import { EmptyState, LoadingState } from '../components/States';
import { useToast } from '../lib/toast';

const SYNC_ENDPOINT: string = import.meta.env.VITE_SYNC_ENDPOINT ?? '';
/** All PDFs are global; the worker keys them under this pseudo-category. */
const GLOBAL = '__all__';
const MAX_MB = 20;

async function callWorker<T>(action: string, extra: Record<string, unknown> = {}): Promise<T> {
  if (!SYNC_ENDPOINT) throw new Error('VITE_SYNC_ENDPOINT is not configured.');
  const idToken = await auth.currentUser?.getIdToken();
  const res = await fetch(SYNC_ENDPOINT, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ action, idToken, ...extra }),
  });
  const data = (await res.json()) as T & { ok: boolean; message?: string };
  if (!data.ok) throw new Error(data.message ?? `${action} failed`);
  return data;
}

/** URL-safe id from the title, kept unique against existing entries. */
function makeId(title: string, taken: Set<string>): string {
  const base =
    title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 32) || 'pdf';
  let id = base;
  let n = 2;
  while (taken.has(id)) id = `${base}-${n++}`;
  return id;
}

export function PdfsPage(): JSX.Element {
  const toast = useToast();
  const [config, setConfig] = useState<OffersConfig | null | undefined>(undefined);
  const [title, setTitle] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    const ref = doc(db, COLLECTIONS.appConfig, OFFERS_DOC);
    return onSnapshot(
      ref,
      (snap) => setConfig((snap.data() as OffersConfig | undefined) ?? null),
      () => setConfig(null),
    );
  }, []);

  /** Current list, including legacy single-PDF entries so they're manageable. */
  const items: OfferPdf[] = (() => {
    if (!config) return [];
    if (config.items?.length) return config.items.slice().sort((a, b) => a.order - b.order);
    const legacy: OfferPdf[] = [];
    if (config.warranty)
      legacy.push({ id: 'warranty', title: 'Warranty Offer', url: config.warranty, order: 0, updatedAt: 0 });
    if (config.cashback)
      legacy.push({ id: 'cashback', title: 'Cash Back Offer', url: config.cashback, order: 1, updatedAt: 0 });
    return legacy;
  })();

  const persist = async (next: OfferPdf[]): Promise<void> => {
    await setDoc(
      doc(db, COLLECTIONS.appConfig, OFFERS_DOC),
      {
        items: next.map((it, i) => ({ ...it, order: i })),
        // Retire the legacy fields once the list is in use.
        warranty: null,
        cashback: null,
        updatedAt: Date.now(),
      },
      { merge: true },
    );
  };

  const add = async (): Promise<void> => {
    const trimmed = title.trim();
    if (!trimmed) return toast.error('Give the button a title first.');
    if (!file) return toast.error('Choose a PDF file.');
    if (file.type !== 'application/pdf') return toast.error('That file is not a PDF.');
    if (file.size > MAX_MB * 1024 * 1024) return toast.error(`PDF must be under ${MAX_MB} MB.`);

    setBusy('add');
    try {
      const id = makeId(trimmed, new Set(items.map((i) => i.id)));
      const dataBase64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
        reader.onerror = () => reject(new Error('Could not read the file.'));
        reader.readAsDataURL(file);
      });
      const r = await callWorker<{ url: string }>('upload_offer', {
        category: GLOBAL,
        type: id,
        dataBase64,
      });
      await persist([
        ...items,
        { id, title: trimmed, url: r.url, order: items.length, updatedAt: Date.now() },
      ]);
      setTitle('');
      setFile(null);
      toast.success(`"${trimmed}" is now live in the app.`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Upload failed.');
    } finally {
      setBusy(null);
    }
  };

  const replace = async (item: OfferPdf, newFile: File): Promise<void> => {
    if (newFile.type !== 'application/pdf') return toast.error('That file is not a PDF.');
    if (newFile.size > MAX_MB * 1024 * 1024) return toast.error(`PDF must be under ${MAX_MB} MB.`);
    setBusy(item.id);
    try {
      const dataBase64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
        reader.onerror = () => reject(new Error('Could not read the file.'));
        reader.readAsDataURL(newFile);
      });
      const r = await callWorker<{ url: string }>('upload_offer', {
        category: GLOBAL,
        type: item.id,
        dataBase64,
      });
      await persist(
        items.map((it) => (it.id === item.id ? { ...it, url: r.url, updatedAt: Date.now() } : it)),
      );
      toast.success(`"${item.title}" replaced.`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Replace failed.');
    } finally {
      setBusy(null);
    }
  };

  const rename = async (item: OfferPdf, newTitle: string): Promise<void> => {
    const trimmed = newTitle.trim();
    if (!trimmed || trimmed === item.title) return;
    setBusy(item.id);
    try {
      await persist(items.map((it) => (it.id === item.id ? { ...it, title: trimmed } : it)));
      toast.success('Button title updated.');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Rename failed.');
    } finally {
      setBusy(null);
    }
  };

  const remove = async (item: OfferPdf): Promise<void> => {
    if (!window.confirm(`Remove "${item.title}"? The button disappears from the app.`)) return;
    setBusy(item.id);
    try {
      await callWorker('delete_offer', { category: GLOBAL, type: item.id });
      await persist(items.filter((it) => it.id !== item.id));
      toast.success(`"${item.title}" removed.`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Remove failed.');
    } finally {
      setBusy(null);
    }
  };

  const move = async (index: number, dir: -1 | 1): Promise<void> => {
    const j = index + dir;
    if (j < 0 || j >= items.length) return;
    const next = items.slice();
    [next[index], next[j]] = [next[j], next[index]];
    setBusy('order');
    try {
      await persist(next);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-6">
      <SectionHeader
        title="PDFs"
        subtitle="Documents dealers can share from the app — one button each, in this order."
      />

      <Card>
        <div className="border-b border-slate-100 px-5 py-3 text-sm font-semibold text-slate-700">
          Add a PDF
        </div>
        <div className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-end">
          <div className="flex-1">
            <label className="mb-1 block text-xs font-medium text-slate-500" htmlFor="pdf-title">
              Button title
            </label>
            <input
              id="pdf-title"
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
              placeholder="e.g. Warranty Offer"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={40}
            />
          </div>
          <div className="flex-1">
            <label className="mb-1 block text-xs font-medium text-slate-500" htmlFor="pdf-file">
              PDF file (max {MAX_MB} MB)
            </label>
            <input
              id="pdf-file"
              type="file"
              accept="application/pdf"
              className="block w-full text-xs text-slate-500 file:mr-2 file:rounded-md file:border-0 file:bg-slate-100 file:px-2 file:py-1.5 file:text-xs file:font-semibold file:text-slate-700"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
          </div>
          <Button variant="primary" onClick={() => void add()} disabled={busy !== null}>
            {busy === 'add' ? 'Uploading…' : 'Add PDF'}
          </Button>
        </div>
      </Card>

      {config === undefined ? (
        <LoadingState label="Loading PDFs…" />
      ) : items.length === 0 ? (
        <EmptyState
          title="No PDFs yet."
          hint="Add one above and a share button appears in the dealer app immediately."
          icon="📄"
        />
      ) : (
        <Card>
          <div className="border-b border-slate-100 px-5 py-3 text-sm font-semibold text-slate-700">
            Live in the app ({items.length})
          </div>
          <ul className="divide-y divide-slate-100">
            {items.map((item, i) => (
              <li key={item.id} className="flex flex-wrap items-center gap-3 px-5 py-3">
                <input
                  className="min-w-[160px] flex-1 rounded-md border border-transparent px-2 py-1 text-sm font-medium text-slate-800 hover:border-slate-200 focus:border-brand-500 focus:outline-none"
                  defaultValue={item.title}
                  maxLength={40}
                  onBlur={(e) => void rename(item, e.target.value)}
                  aria-label="Button title"
                />
                <a
                  href={item.url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs font-medium text-brand-600 hover:underline"
                >
                  View
                </a>
                <label className="cursor-pointer text-xs font-medium text-slate-500 hover:underline">
                  Replace
                  <input
                    type="file"
                    accept="application/pdf"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      e.target.value = '';
                      if (f) void replace(item, f);
                    }}
                  />
                </label>
                <button
                  type="button"
                  className="rounded px-1.5 py-1 text-slate-400 hover:bg-slate-100 disabled:opacity-30"
                  disabled={i === 0 || busy !== null}
                  onClick={() => void move(i, -1)}
                  aria-label="Move up"
                >
                  ↑
                </button>
                <button
                  type="button"
                  className="rounded px-1.5 py-1 text-slate-400 hover:bg-slate-100 disabled:opacity-30"
                  disabled={i === items.length - 1 || busy !== null}
                  onClick={() => void move(i, 1)}
                  aria-label="Move down"
                >
                  ↓
                </button>
                <Button
                  variant="danger"
                  size="sm"
                  disabled={busy !== null}
                  loading={busy === item.id}
                  onClick={() => void remove(item)}
                >
                  Remove
                </Button>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}
